import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { CircularProgress, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import { Label } from '@components/Label';
import { Button } from '@components/index';
import { StatusChip } from '@pages/customer/common/statusChip/StatusChip';
import authStore from '@stores/AuthStore';
import { useStore } from '@stores/RootStore';
import { CheckIcon } from '@icons/CheckIcon';
import { ChevronIcon } from '@icons/ChevronIcon';
import { useUpdateApprovalStepStatus } from '@queryHooks/useApproval';
import {
  useGetSubscriptionUseRequest,
  useUpdateStatusSubscriptionUseRequest,
} from '@queryHooks/useSubscriptionUseRequest';
import { dateFormat } from '@utils/dateUtil';
import type { SubscriptionUseRequestHistoryModel } from '@repositories/subscriptionUseRequestRepository/models/SubscriptionUseRequestHistoryModel';
import * as S from './UsageRegisterDetailStyles';
import { useUserForm } from './validationSchema';

type Status = 'APPROVED' | 'REJECTED' | 'PENDING' | 'IN_PROGRESS';

export const UsageRegisterDetail = observer(() => {
  const theme = useTheme();
  const { t } = useTranslation();
  const {
    uiStore: { alertStore: modal },
    categoryStore,
  } = useStore();
  const params = useParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState<Status | undefined>(undefined);
  const { data: usageRq, isLoading, isError } = useGetSubscriptionUseRequest(params.id ?? '');

  const handleClickBtn = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (status === e.currentTarget.id) setStatus(undefined);
    else setStatus(e.currentTarget.id as Status);
  };

  const { register, getValues } = useUserForm();
  const { mutate: updateUseRqStatus } = useUpdateStatusSubscriptionUseRequest();
  const { mutate: updateApprovalStepStatus } = useUpdateApprovalStepStatus();

  const isAdminDecisionRequired =
    !usageRq?.isCompleted && (!usageRq?.approval || usageRq.approval.status === 'APPROVED');
  const isMyApprovalStep =
    usageRq?.approval &&
    usageRq.approval.steps.find(step => step.approverId === authStore.id)?.status === 'IN_PROGRESS';

  const validation = {
    success: () => {
      const comment = getValues('comment');
      if (status)
        if (isMyApprovalStep) {
          const currentStep = usageRq.approval.steps.find(step => step.approverId === authStore.id);
          const action = status === 'APPROVED' ? 'APPROVED' : 'REJECTED';

          updateApprovalStepStatus(
            {
              approvalId: usageRq.approval.id,
              approvalStepId: currentStep?.id ?? '',
              dto: {
                status: action,
                comment: comment.trim(),
              },
            },
            {
              onSuccess: () => {
                navigate(`/manager/${params.tenantId}/subscriptions/usage-registers`);
              },
            },
          );
        } else {
          updateUseRqStatus(
            { requestId: params.id as string, rqData: { status, comment } },
            {
              onSuccess: () => {
                navigate(`/manager/${params.tenantId}/subscriptions/usage-registers`);
              },
            },
          );
        }
    },
  };

  useEffect(() => {
    if (usageRq?.status !== 'CREATED') setStatus(usageRq?.status);
    else setStatus(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usageRq]);

  const productType = usageRq?.categoryId ? categoryStore.findRootCategory(usageRq.categoryId)?.name : '';

  if (isLoading || isError) return <CircularProgress />;

  return (
    <S.Container>
      <S.Header onClick={() => navigate(-1)}>
        <ChevronIcon rotateNum={270} />
        {t('Subscrib_GNB_04')}
      </S.Header>
      <S.RequestWrapper>
        <S.Title>{t('Subscrib_Detail_UseRequest_27')}</S.Title>
        <S.RequestContents>
          <div>
            <strong className="content-name">{t('Subscrib_Detail_UseRequest_06')}</strong>
            <span className="content-value">{productType}</span>
          </div>
          <div>
            <strong className="content-name">{t('Subscrib_Detail_UseRequest_05')}</strong>
            <span className="content-value">{dayjs(usageRq?.createdDate).format(t('DateFormat_YMD'))}</span>
          </div>
        </S.RequestContents>
        <S.RequestContents>
          <div>
            <strong className="content-name">{t('Subscrib_Detail_UseRequest_07')}</strong>
            <span className="content-value">{usageRq?.productName}</span>
          </div>
          <div>
            <strong className="content-name">{t('Subscrib_Detail_UseRequest_08')}</strong>
            <span className="content-value">{usageRq?.userName}</span>
          </div>
        </S.RequestContents>
        <S.RequestContents>
          <div>
            <strong className="content-name">{t('Subscrib_Main_12')}</strong>
            <span className="content-value">
              {usageRq?.planName}
              {usageRq?.isInUseByTenant && (
                <S.StylredLabel variant="contain" color="gradient">
                  {t('Ad_Store_Product_State')}
                </S.StylredLabel>
              )}
            </span>
          </div>
          <div>
            <strong className="content-name">{t('Product_Use_Request_14')}</strong>
            <span className="content-value">{usageRq?.subscription ? usageRq?.subscription.name : '-'}</span>
          </div>
        </S.RequestContents>
        <S.RequestContents>
          <div>
            <strong className="content-name">{t('Member_User_Bulk_Create_18')}</strong>
            <span className="content-value">
              {usageRq?.userGroup !== undefined && usageRq?.userGroup.length > 0 ? usageRq?.userGroup[0].name : '-'}
            </span>
          </div>
          <div>
            <strong className="content-name">{t('Acc_Detail_04')}</strong>
            <span className="content-value">{`${dateFormat(
              usageRq?.startDate,
              '-',
              t('DateFormat_YMD'),
            )} ~ ${dateFormat(usageRq?.endDate, '-', t('DateFormat_YMD'))}`}</span>
          </div>
        </S.RequestContents>
        <S.RequestContents>
          <div className="inquiry">
            <strong className="content-name">{t('Subscrib_Detail_UseRequest_15')}</strong>
            <span className="content-value">{usageRq?.inquiry}</span>
          </div>
        </S.RequestContents>
      </S.RequestWrapper>
      {usageRq.approval && (
        <S.RequestWrapper>
          <S.Title>{t('Approval_Detail_Process_History')}</S.Title>

          <ApprovalStepContainer>
            <ApprovalStepNumber>{0}</ApprovalStepNumber>
            <ApprovalStepContent>
              <ApproverInfo>{usageRq.approval.drafterName}</ApproverInfo>
              <ApprovalStepStatusContainer>
                <StatusChip
                  status={t('Approval_Status_Draft')}
                  backgroundColor={theme.colors['bg-gray-dark']}
                  color={theme.colors['text-white']}
                />
                <ApprovalStepUpdatedAt>{usageRq.approval.createdDate}</ApprovalStepUpdatedAt>
              </ApprovalStepStatusContainer>
            </ApprovalStepContent>
          </ApprovalStepContainer>
          {usageRq.approval.steps
            .sort((a, b) => a.stepOrder - b.stepOrder)
            .map(step => (
              <ApprovalStepContainer key={step.id}>
                <ApprovalStepNumber>{step.stepOrder + 1}</ApprovalStepNumber>
                <ApprovalStepContent>
                  <ApproverInfo> {step.approverInfoLabel}</ApproverInfo>
                  {step.status !== 'WAITING' && (
                    <>
                      <ApprovalStepStatusContainer>
                        <StatusChip
                          status={step.statusLabel}
                          backgroundColor={
                            theme.colors[step.statusBackgroundColor as keyof typeof theme.colors] as string
                          }
                          color={theme.colors[step.statusTextColor as keyof typeof theme.colors] as string}
                        />
                        <ApprovalStepUpdatedAt>{step.approvedAt ?? '-'}</ApprovalStepUpdatedAt>
                      </ApprovalStepStatusContainer>
                      <ApprovalStepComment>{step.comment ?? '-'}</ApprovalStepComment>
                    </>
                  )}
                </ApprovalStepContent>
              </ApprovalStepContainer>
            ))}
        </S.RequestWrapper>
      )}
      {usageRq.isCompleted && !!usageRq.completeHistory && (
        <CompleteRequestReply completeHistory={usageRq.completeHistory} />
      )}
      {(isAdminDecisionRequired || isMyApprovalStep) && (
        <S.RequestReplyWrapper>
          <S.TitleWithDescription>{t('Subscrib_Detail_UseRequest_16')}</S.TitleWithDescription>
          <S.PendingDescription>{t('Subscrib_Detail_UseRequest_17')}</S.PendingDescription>
          <S.Buttons>
            <S.StyledButton
              id="APPROVED"
              className={status === 'APPROVED' ? 'contain' : 'outline'}
              variant={status === 'APPROVED' ? 'contain' : 'outline'}
              onClick={handleClickBtn}
              size="extraLarge"
              startIcon={status === 'APPROVED' && <CheckIcon width={16} height={16} color={theme.colors['ic-white']} />}
            >
              {t('Subscrib_Detail_UseRequest_37')}
            </S.StyledButton>
            <S.StyledButton
              id="REJECTED"
              className={
                status === 'APPROVED'
                  ? 'approved'
                  : status === 'PENDING'
                  ? 'pending'
                  : status === 'REJECTED'
                  ? 'rejected contain'
                  : ''
              }
              variant={status === 'REJECTED' ? 'contain' : 'outline'}
              onClick={handleClickBtn}
              size="extraLarge"
              startIcon={status === 'REJECTED' && <CheckIcon width={16} height={16} color={theme.colors['ic-white']} />}
            >
              {t('Subscrib_Detail_UseRequest_38')}
            </S.StyledButton>
            <S.StyledButton
              id="PENDING"
              className={status === 'PENDING' ? 'contain' : 'outline'}
              variant={status === 'PENDING' ? 'contain' : 'outline'}
              onClick={handleClickBtn}
              size="extraLarge"
              startIcon={status === 'PENDING' && <CheckIcon width={16} height={16} color={theme.colors['ic-white']} />}
              disabled={isMyApprovalStep}
            >
              {t('Subscrib_Detail_UseRequest_39')}
            </S.StyledButton>
          </S.Buttons>
          <S.SubTitle>{t('Subscrib_Detail_UseRequest_15')}</S.SubTitle>
          <S.StyledTextField
            {...register('comment')}
            multiline
            rows={6}
            placeholder={
              status === 'APPROVED'
                ? t('Subscrib_Detail_UseRequest_28')
                : status === 'PENDING'
                ? t('Subscrib_Detail_UseRequest_29')
                : status === 'REJECTED'
                ? t('Subscrib_Detail_UseRequest_30')
                : t('Subscrib_Detail_UseRequest_31')
            }
          />
          <S.ButtonWrapper>
            <Button
              paddingHorizontal={16}
              variant="contain"
              size="large"
              onClick={() =>
                modal.open({
                  title: t('Subscrib_Detail_UseRequest_21'),
                  message: t('Subscrib_Detail_UseRequest_22'),
                  useCancel: true,
                  onConfirm: validation.success,
                })
              }
              disabled={status === undefined}
            >
              {t('Subscrib_Detail_UseRequest_18')}
            </Button>
          </S.ButtonWrapper>
        </S.RequestReplyWrapper>
      )}
    </S.Container>
  );
});

type CompleteRequestReplyProps = {
  completeHistory: SubscriptionUseRequestHistoryModel;
};

export const CompleteRequestReply = ({ completeHistory }: CompleteRequestReplyProps) => {
  const { t } = useTranslation();
  return (
    <S.RequestReplyWrapper>
      <S.Title>{t('Request_Processing')}</S.Title>
      <S.RequestContents>
        <div>
          <strong className="content-name">{t('Subscrib_Detail_UseRequest_09')}</strong>
          <span className="content-value">
            <Label variant="contain" color={completeHistory.statusLabelColor}>
              {completeHistory.statusStr}
            </Label>
          </span>
        </div>
        <div>
          <strong className="content-name">{t('Subscrib_Detail_UseRequest_19')}</strong>
          <span className="content-value">{dayjs(completeHistory.createdDate).format(t('DateFormat_YMD'))}</span>
        </div>
      </S.RequestContents>
      <S.RequestContents>
        <div>
          <strong className="content-name">{t('Acc_Detail_12')}</strong>
          <span className="content-value">{completeHistory.managerName}</span>
        </div>
      </S.RequestContents>
      <S.RequestInquiry>
        <div>
          <strong className="content-name">{t('Subscrib_Detail_BuyRequest_05')}</strong>
          <p className="content-value">{completeHistory.comment}</p>
        </div>
      </S.RequestInquiry>
    </S.RequestReplyWrapper>
  );
};

const ApprovalStepContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 20px;
  padding: 15px 20px;
  border-bottom: 1px solid ${({ theme: { colors } }) => colors['border-gray-lighter']};
  background-color: ${({ theme: { colors } }) => colors['bg-white']};
`;

const ApprovalStepNumber = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 2px;
  background-color: ${({ theme: { colors } }) => colors['bg-gray-light']};
  color: ${({ theme: { colors } }) => colors['text-gray-sub-darker']};
  font-family: 'Pretendard';
  font-size: 13px;
  font-weight: 700;
  line-height: 1.54;
  letter-spacing: 0.015em;
  flex-shrink: 0;
`;

const ApprovalStepContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
`;

const ApproverInfo = styled.div`
  font-family: 'Pretendard';
  font-size: 14px;
  font-weight: 700;
  line-height: 1.43;
  letter-spacing: 0.011em;
  color: ${({ theme: { colors } }) => colors['text-gray-main']};
`;

const ApprovalStepStatusContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const ApprovalStepUpdatedAt = styled.span`
  font-family: 'Pretendard';
  font-size: 13px;
  font-weight: 700;
  line-height: 1.54;
  letter-spacing: 0.012em;
  color: ${({ theme: { colors } }) => colors['text-gray-sub-darker']};
  width: 116px;
`;

const ApprovalStepComment = styled.div`
  font-family: 'Pretendard';
  font-size: 13px;
  font-weight: 500;
  line-height: 1.54;
  letter-spacing: 0.012em;
  color: ${({ theme: { colors } }) => colors['text-gray-main']};
  word-break: break-word;
`;
